/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/display-name */

import React from 'react';
import { TableComponents, TableVirtuoso } from 'react-virtuoso';
import './index.css';
import { Paper, Table, TableBody, TableCell, TableContainer, TableRow, TableHead, useTheme, CircularProgress } from '@mui/material';
import useScreenSize from 'common/hooks/useScreenSize';
import UserDataTableProps from 'common/types/UserDataTableProps';
import { genderList } from 'common/data/genderList';
import { useNavigate } from 'react-router-dom';



interface ColumnData {
  dataKey: keyof UserDataTableProps | 'type';
  label: string;
  numeric?: boolean;
  width: number;
  isName?: boolean;
}

// type Sample = [number, string, string, boolean, string, string, string, string, string, string, string, string, number, string, string];

// const sample: readonly Sample[] = [
//   [1, 'John', 'Doe', true, 'johndoe@example.com', '', '01/01/1990', 'New York', 'Some additional info', '', '1234', '5678', 0.1, 'company1', 'Admin'],
//   [2, 'Jane', '', false, '', '555-5678', '02/02/1995', 'Los Angeles', '', '', '5678', '9012', 0.2, 'company2', 'Designer'],
//   [3, 'Bob', 'Smith', true, 'bobsmith@example.com', '555-9012', '03/03/2000', 'Chicago', 'Some additional info', 'Male', '2345', '6789', 0.3, 'company3', 'Developer'],
//   [4, 'Alice', 'Johnson', false, 'alicejohnson@example.com', '555-6789', '04/04/2005', 'Houston', '', 'Female', '3456', '7890', 0.4, 'company4', 'Partner'],
//   [5, 'Mike', 'Brown', true, 'mikebrown@example.com', '555-7890', '05/05/2010', 'Miami', 'Some additional info', 'Male', '4567', '8901', 0.5, 'company5', 'Plumber'],
// ];

// function createData(
//   id: number,
//   firstName: string,
//   lastName: string,
//   type: boolean,
//   email: string,
//   phone: string,
//   birthDate: string,
//   location: string,
//   additionalInformation: string,
//   gender: string,
//   companyId: string,
//   cardNumber: string,
//   discount: number,
//   clientCompanyId: string,
//   role?: string,
// ): UserDataProps {
//   return { id, firstName, lastName, type, role, email, phone, birthDate, location, additionalInformation, gender, companyId, cardNumber, discount, clientCompanyId };
// }

// ПІБ / Назва, Тип, Роль, Стать, Д/Н, Локація, Контакти, Додаткове

const columns: ColumnData[] = [
  {
    width: 248,
    label: 'ПІБ / Назва',
    dataKey: 'lastName',
    isName: true,
  },
  {
    width: 73,
    label: 'Тип',
    dataKey: 'type',
  },
  {
    width: 136,
    label: 'Роль',
    dataKey: 'role',
  },
  {
    width: 95,
    label: 'Стать',
    dataKey: 'gender',
  },
  {
    width: 136,
    label: 'Д/Н',
    dataKey: 'birthDate',
  },
  {
    width: 183,
    label: 'Локація',
    dataKey: 'country',
  },
  {
    width: 210,
    label: 'Контакти',
    dataKey: 'phoneNumber',
  },
  {
    width: 231,
    label: 'Додаткове',
    dataKey: 'additionalInformation',
  },
];

/*const rows: UserDataProps[] = Array.from({ length: 100 }, () => {
  const randomSelection = sample[Math.floor(Math.random() * sample.length)];
  return createData(...randomSelection);
});*/

const VirtuosoTableComponents: TableComponents<UserDataTableProps> = {
  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
    <TableContainer sx={{
      '&::-webkit-scrollbar': {
        width: '14px',
        padding: '15px 0',
        transform: 'scale(0.5)',
      },
      '&::-webkit-scrollbar-track': {
        background: 'rgba(0, 0, 0, 0)',
      },
      '&::-webkit-scrollbar-corner': {
        background: 'rgba(0, 0, 0, 0)',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'rgba(64, 108, 255, 0.10)',
        borderRadius: '20px',
        border: '3px solid rgba(0, 0, 0, 0)',
        backgroundClip: 'padding-box',
      },
    }} component={Paper} {...props} ref={ref} />
  )),
  Table: (props) => (
    <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
  ),
  TableHead,
  // eslint-disable-next-line react/prop-types
  TableRow: ({ item: _item, ...props }) => <TableRow sx={{
    height: '72px',
    maxHeight: '72px',
    cursor: 'pointer',
    '& td': {
      transition: 'background-color .2s ease-in-out',
    },
    '&:hover td': {
      backgroundColor: 'rgba(86, 120, 255, 0.25)',
    },
  }} {...props} />,
  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

function fixedHeaderContent() {
  const theme = useTheme();
  return (
    <TableRow sx={{ backgroundColor: theme.colors.secondaryColor }}>
      {columns.map((column) => (
        <TableCell
          key={column.dataKey}
          variant="head"
          align={column.numeric || false ? 'right' : 'left'}
          style={{
            width: column.width,
            fontSize: '20px',
            fontStyle: 'normal',
            fontWeight: '500',
            padding: '0px',
            height: '36px',
            verticalAlign: 'top',
            lineHeight: '100%',
            letterSpacing: '1px',
            color: theme.colors.black,
          }}
          className='table-header-cell'
          sx={{
            backgroundColor: 'transparent',
            borderBottom: 'none',
            textAlign: 'center',
            '&:not(:last-of-type)': {
              borderRight: `1px solid ${theme.colors.gray}`,
            },
            '&:first-of-type .table-head-cell': {
              width: 'calc(100% - 8px)',
              marginLeft: '8px',
            },
            '&:last-of-type .table-head-cell': {
              width: 'calc(100% - 8px)',
            },
          }}
        >
          <div style={{ borderColor: theme.colors.gray }} className='table-head-cell'>
            {column?.label}
          </div>
        </TableCell>
      ))}
    </TableRow>
  );
}



type Props = {
  searchValue?: string;
  data: UserDataTableProps[];
  loading?: boolean;
};

export default function CustomTable({ searchValue, data, loading }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const screenSize = useScreenSize();
  // Filter data by full name, by number and email
  const filteredData = data.filter((row) => {
    const fullName = `${row.firstName} ${row.lastName}`;
    const phone = row.phoneNumber || '';
    const email = row.email || '';
    const search = searchValue?.toLowerCase() || '';
    return fullName.toLowerCase().includes(search || '') || phone.includes(search || '') || email.includes(search || '');
  });

  function rowContent(_index: number, row: UserDataTableProps) {
    return (
      <React.Fragment>
        {columns.map((column) => {
          let result: number | string | boolean | React.ReactNode = row[column.dataKey];
          if (column.dataKey === 'lastName') {
            // LastName F.
            if (row.lastName?.length > 0) {
              result = `${row.lastName} ${row.firstName[0]}.${row.patronymic?.length > 0 ? row.patronymic[0] + '.' : ''}`;
            } else {
              result = row.firstName;
            }
            // Company name
            if (row.companyName?.length > 0) {
              result = row.companyName;
            }
          }
          if (column.dataKey === 'gender') {
            result = genderList[row.gender - 1]?.tableLabel;
            if (row.gender === 0 || row.gender >= 4) {
              result = '—';
            }
          }
          if (result === undefined || result === null || result === '') {
            result = '—';
            if (column.dataKey === 'phoneNumber' && row.email?.length > 0) {
              result = row.email;
            }
            if (column.dataKey === 'country' && row.city?.length > 0) {
              result = row.city;
            }
          }
          // Change 2000-01-01T00:00:00 to 01/01/2000
          if (column.dataKey === 'birthDate' && row.birthDate?.length > 0) {
            result = row.birthDate?.split('T')[0].split('-').reverse().join('/');
          }
          if (column.dataKey === 'type') {
            result = row.companyName.length > 1 ? 'Ю' : 'Ф';
          }
  
          let resultJSX = <span>{result}</span>;
  
          if (column.dataKey === 'phoneNumber' && row.phoneNumber?.length > 0 && row.email?.length > 0) {
            resultJSX = (
              <div className='table-multiline'>
                <span>{row.phoneNumber}</span>
                <span>{row.email}</span>
              </div>
            );
          }
          if (column.dataKey === 'country' && row.country?.length > 0 && row.city?.length > 0) {
            resultJSX = (
              <div className='table-multiline'>
                <span>{row.country + ','}</span>
                <span>{row.city}</span>
              </div>
            );
          }
  
          return (
            <TableCell
              key={column.dataKey}
              align={column.numeric || false ? 'right' : 'left'}
              className='table-cell'
              onClick={() => {navigate(row.id.toString(), { state:{ type: row.companyName ? true : false, phone: row.phoneNumber } });}}
              sx={{
                color: theme.colors.black,
                borderBottom: 'none',
                verticalAlign: 'top',
                padding: '0',
                '&:not(:first-of-type)': {
                  textAlign: 'center',
                },
                '&:not(:last-of-type)': {
                  borderRight: `1px solid ${theme.colors.gray}`,
                },
                '&:first-of-type': {
                  paddingLeft: '8px',
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                },
                '&:last-of-type': {
                  paddingRight: '8px',
                  borderTopRightRadius: '8px',
                  borderBottomRightRadius: '8px',
                },
                '&:first-of-type .table-cell-border': {
                  justifyContent: 'flex-start',
                },
              }}
            >
              <div style={{ borderColor: theme.colors.gray }} className='table-cell-border'>
                {resultJSX}
              </div>
            </TableCell>
          );
        })}
      </React.Fragment>
    );
  }

  return (
    <Paper sx={{
      backgroundColor: 'transparent',
      boxShadow: 'unset',
      borderRadius: 16 + 'px',
      border: `1px solid ${theme.colors.gray}`,
      overflow: 'hidden',
      padding: '16px 8px',
      color: theme.colors.black,
      position: 'relative',

    }} style={{ height: screenSize.height - 254 + 52 - 16, width: '100%' }}>
      {!loading ? <TableVirtuoso
        data={filteredData}
        style={{ boxShadow: 'unset', backgroundColor: 'transparent', color: theme.colors.black }}
        components={VirtuosoTableComponents}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={rowContent}
      /> : <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}><CircularProgress sx={{

      }} /></div>}
    </Paper>
  );
}